import React, { useState, useEffect } from 'react';
import { Layout, Button, Drawer } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import '../styles/AppleStyle.css';

const { Header } = Layout;

interface NavItem {
  key: string;
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { key: 'home', label: '首页', href: '#home' },
  { key: 'service-intro', label: '服务介绍', href: '#service-intro' },
  { key: 'process', label: '服务流程', href: '#process' },
  { key: 'flight-info', label: '航班信息', href: '#flight-info' },
  { key: 'customs-airports', label: '清关机场', href: '#customs-airports' },
  { key: 'advantages', label: '专线优势', href: '#advantages' },
  { key: 'pricing', label: '运费报价', href: '#pricing' },
  { key: 'small-parcel', label: '小货服务', href: '#small-parcel' },
  { key: 'special-cargo', label: '特殊货物', href: '#special-cargo' },
  { key: 'anti-dumping', label: '反倾销警示', href: '#anti-dumping' },
  { key: 'cases', label: '成功案例', href: '#cases' },
  { key: 'blog', label: '行业资讯', href: '#blog' },
  { key: 'contact', label: '联系我们', href: '#contact' },
];

const AppleNavBar: React.FC = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      
      // 检测当前活动区域
      const sections = navItems.map(item => item.key);
      let current = 'home';
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            current = section;
          }
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <Header 
        className="nav-apple"
        style={{
          height: '72px',
          lineHeight: '72px',
          padding: '0',
          background: isScrolled 
            ? 'rgba(255, 255, 255, 0.8)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${isScrolled ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.05)'}`,
          transition: 'all 0.3s ease',
        }}
      >
        <div className="container-wide" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          height: '100%',
          gap: '40px'
        }}>
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px',
              flexShrink: 0,
              minWidth: 'auto'
            }}
          >
            <div style={{
              width: '36px',
              height: '36px',
              background: '#0071e3',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              ✈
            </div>
            <div>
              <div style={{ 
                fontSize: '20px', 
                fontWeight: '700',
                color: '#1D1D1F',
                lineHeight: '1.2'
              }}>
                美国空派专线
              </div>
              <div style={{
                fontSize: '12px',
                color: '#6e6e73',
                lineHeight: '1',
                marginTop: '-2px'
              }}>
                US Air Express
              </div>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ 
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
              minWidth: 0
            }}
            className="desktop-nav"
          >
            <div style={{ display: 'flex', gap: '1px', flexWrap: 'nowrap' }}>
              {navItems.map((item) => (
                <motion.a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    color: activeSection === item.key ? '#0071e3' : '#1d1d1f',
                    backgroundColor: activeSection === item.key ? 'rgba(0, 113, 227, 0.08)' : 'transparent',
                    fontSize: '15px',
                    fontWeight: activeSection === item.key ? '600' : '500',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.label}
                  {activeSection === item.key && (
                    <motion.div
                      layoutId="activeIndicator"
                      style={{
                        position: 'absolute',
                        bottom: '-8px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '4px',
                        height: '4px',
                        backgroundColor: '#0071e3',
                        borderRadius: '50%'
                      }}
                    />
                  )}
                </motion.a>
              ))}
            </div>
          </motion.nav>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ 
              display: 'flex',
              flexShrink: 0
            }}
            className="desktop-nav"
          >
            <Button 
              type="primary" 
              size="large"
              className="btn-primary"
              onClick={() => handleNavClick('#contact')}
              style={{
                background: '#0071e3',
                border: 'none',
                borderRadius: '8px',
                height: '44px',
                fontWeight: '600',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)'
              }}
            >
              立即咨询
            </Button>
          </motion.div>

          {/* Mobile Menu Button */}
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuOpen(true)}
            className="mobile-menu-btn"
            style={{
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              color: '#1D1D1F'
            }}
          />
        </div>
      </Header>

      {/* Mobile Drawer */}
      <Drawer
        title={null}
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={300}
        className="mobile-nav-drawer"
        styles={{
          body: { padding: '24px' },
          header: { display: 'none' }
        }}
      >
        <div style={{ marginBottom: '32px' }}>
          <div style={{ 
            fontSize: '24px', 
            fontWeight: '700',
            color: '#1D1D1F',
            marginBottom: '4px'
          }}>
            美国空派专线
          </div>
          <div style={{ fontSize: '14px', color: '#6e6e73' }}>
            US Air Express
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {navItems.map((item) => (
            <motion.a
              key={item.key}
              href={item.href}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(item.href);
              }}
              whileTap={{ scale: 0.98 }}
              style={{
                padding: '16px',
                borderRadius: '12px',
                textDecoration: 'none',
                color: activeSection === item.key ? '#0071e3' : '#1d1d1f',
                backgroundColor: activeSection === item.key ? 'rgba(0, 113, 227, 0.08)' : 'transparent',
                fontSize: '18px',
                fontWeight: activeSection === item.key ? '600' : '500',
                transition: 'all 0.2s ease',
                border: activeSection === item.key ? '1px solid #d2d2d7' : '1px solid transparent'
              }}
            >
              {item.label}
            </motion.a>
          ))}
        </div>
        
        <Button
          type="primary"
          size="large"
          block
          onClick={() => handleNavClick('#contact')}
          style={{
            marginTop: '32px',
            background: '#0071e3',
            border: 'none',
            borderRadius: '8px',
            height: '48px',
            fontSize: '18px',
            fontWeight: '600'
          }}
        >
          立即咨询
        </Button>
      </Drawer>


    </>
  );
};

export default AppleNavBar;
